// components/auth/PasswordReset.jsx
import React, { useState } from 'react';
import { resetPasswordWithRecovery } from '../../utils/crypto';
import config from '@/config';

const PasswordReset = ({ onSuccess }) => {
  const [step, setStep] = useState('email'); // email, recovery, newPassword
  const [email, setEmail] = useState('');
  const [recoveryKey, setRecoveryKey] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [encryptionData, setEncryptionData] = useState(null);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Request encryption data from backend
      const response = await fetch(`${config.AUTH_API_ADDRESS}auth/request-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Email not found');
      }

      const data = await response.json();
      setEncryptionData(data.encryption_data);
      setStep('recovery');
    } catch (err) {
      setError(err.message || 'Failed to find account');
    } finally {
      setLoading(false);
    }
  };

  const handleRecoveryKeySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Verify recovery key hash on backend first
      const response = await fetch('http://localhost:8000/auth/verify-recovery', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email,
          recovery_key: recoveryKey 
        }),
      });

      if (!response.ok) {
        throw new Error('Invalid recovery key');
      }

      setStep('newPassword');
    } catch (err) {
      setError(err.message || 'Invalid recovery key');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      // Reset password with recovery key
      const result = await resetPasswordWithRecovery(
        recoveryKey,
        newPassword,
        encryptionData
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      // Send new encrypted data to backend
      const response = await fetch('http://localhost:8000/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          ...result
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to reset password');
      }

      // Success
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
        Reset Password
      </h2>

      {step === 'email' && (
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <p className="text-gray-600 mb-4">
            Enter your email address and we'll help you reset your password using your recovery key.
          </p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="you@example.com"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg hover:bg-blue-600 disabled:bg-gray-400 font-semibold"
          >
            {loading ? 'Checking...' : 'Continue'}
          </button>
        </form>
      )}

      {step === 'recovery' && (
        <form onSubmit={handleRecoveryKeySubmit} className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              Enter the recovery key you saved during registration. This key is required to reset your password.
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recovery Key
            </label>
            <textarea
              value={recoveryKey}
              onChange={(e) => setRecoveryKey(e.target.value)}
              required
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder="Enter your recovery key"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg hover:bg-blue-600 disabled:bg-gray-400 font-semibold"
          >
            {loading ? 'Verifying...' : 'Verify Recovery Key'}
          </button>

          <button
            type="button"
            onClick={() => setStep('email')}
            className="w-full text-gray-600 py-2 hover:text-gray-800"
          >
            Back
          </button>
        </form>
      )}

      {step === 'newPassword' && (
        <form onSubmit={handlePasswordReset} className="space-y-4">
          <p className="text-green-600 mb-4">
            ✓ Recovery key verified! Now create a new password.
          </p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Min. 8 characters"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Confirm password"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-green-500 text-white py-3 px-4 rounded-lg hover:bg-green-600 disabled:bg-gray-400 font-semibold"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      )}

      <p className="text-center text-sm text-gray-600 mt-4">
        Remember your password?{' '}
        <a href="/login" className="text-blue-500 hover:underline">
          Back to Login
        </a>
      </p>
    </div>
  );
};

export default PasswordReset;